import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import IncidentCard from './IncidentCard';
import IncidentListRow from './components/IncidentListRow';
import ExpandedContent from './components/ExpandedContent';
import AllClearZeroState from './components/AllClearZeroState';
import RefreshIndicator from './components/RefreshIndicator';
import IncidentErrorBoundary from './components/IncidentErrorBoundary';
import { useIncidentDetails } from './hooks/useIncidentDetails';
import { trackIncidentSectionView } from '../../../utils/modernUITracking';
import styles from './IncidentSection.module.css';

/**
 * IncidentSection - Container for multiple incident cards
 * Features: Responsive grid, loading state, error handling, empty state
 */
const IncidentSection = ({
  title = 'Active Incidents',
  problems = [],
  loading = false,
  refreshing = false,
  lastUpdated = null,
  error = null,
  emptyMessage = 'No active incidents. All systems operational.',
  onRetry = null,
  newrelic = null,
  user = null,
  showTitle = true
}) => {
  // Media query hook for responsive detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Set initial state on mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Accordion state - track which card is expanded (mobile)
  const [expandedCardId, setExpandedCardId] = useState(null);

  // Drawer state for desktop
  const [selectedIncident, setSelectedIncident] = useState(null);

  // Fetch details for selected incident (desktop accordion)
  const {
    data: incidentData,
    loading: incidentLoading,
    refreshing: incidentRefreshing,
    lastUpdated: incidentLastUpdated,
    error: incidentError,
    refresh: refreshIncident
  } = useIncidentDetails(
    selectedIncident?.number,
    !!selectedIncident // Enable when an incident is selected
  );

  // Track page view when component mounts or incident count changes.
  // incidentCount is a primitive so ESLint exhaustive-deps is satisfied while
  // still preventing re-fires on background poll reference changes.
  // Fires when incidentCount === 0 intentionally — the empty state ("all clear")
  // is a meaningful user experience worth tracking separately from non-zero counts.
  const incidentCount = problems.length;
  useEffect(() => {
    if (!loading) {
      trackIncidentSectionView(incidentCount, {
        hasFilters: false // TODO: Add filter state when implemented
      }, newrelic);
    }
  }, [incidentCount, loading, newrelic]);

  // Keyboard navigation for desktop list
  useEffect(() => {
    if (isMobile || !problems || problems.length === 0) return;

    const handleKeyDown = (e) => {
      const currentIndex = problems.findIndex(p => p.number === selectedIncident?.number);

      switch(e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (currentIndex < problems.length - 1) {
            setSelectedIncident(problems[currentIndex + 1]);
          } else if (currentIndex === -1 && problems.length > 0) {
            // No selection, select first
            setSelectedIncident(problems[0]);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (currentIndex > 0) {
            setSelectedIncident(problems[currentIndex - 1]);
          } else if (currentIndex === -1 && problems.length > 0) {
            // No selection, select last
            setSelectedIncident(problems[problems.length - 1]);
          }
          break;

        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, problems, selectedIncident]);

  // Handler to toggle card expansion
  const handleCardToggle = (cardId) => {
    setExpandedCardId(prev => prev === cardId ? null : cardId);
  };

  // Loading state
  if (loading) {
    return (
      <section className={styles.section}>
        {showTitle && title && (
          <h2 className={`tw-text-2xl tw-font-bold tw-mb-6 ${styles.titleText}`}>
            {title}
          </h2>
        )}
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
          <div className={styles.spinner}></div>
          <p className={`tw-mt-4 tw-text-sm ${styles.loadingText}`}>Loading incidents...</p>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className={styles.section}>
        {showTitle && title && (
          <h2 className={`tw-text-2xl tw-font-bold tw-mb-6 ${styles.titleText}`}>
            {title}
          </h2>
        )}
        <div className="tw-bg-red-50 tw-border-l-4 tw-border-red-500 tw-p-6 tw-rounded-lg tw-shadow-sm">
          <div className="tw-flex tw-items-start">
            <svg
              className="tw-w-6 tw-h-6 tw-text-red-500 tw-mr-3 tw-flex-shrink-0 tw-mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="tw-flex-1">
              <h3 className="tw-text-red-800 tw-font-semibold tw-mb-2">
                Failed to load incidents
              </h3>
              <p className="tw-text-red-700 tw-text-sm tw-mb-4">
                {error.message || 'An unexpected error occurred. Please try again.'}
              </p>
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="tw-bg-red-500 hover:tw-bg-red-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition-colors tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-red-500 focus:tw-ring-offset-2"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Empty state
  if (!problems || problems.length === 0) {
    return (
      <section className={styles.section}>
        {showTitle && title && (
          <h2 className={`tw-text-2xl tw-font-bold tw-mb-6 ${styles.titleText}`}>
            {title}
          </h2>
        )}
        <AllClearZeroState />
      </section>
    );
  }

  // Content state - Responsive: Mobile cards vs Desktop list+drawer
  return (
    <section className={styles.section}>
      {showTitle && title && (
        <div className={styles.headerContainer}>
          <h2 className={`tw-text-2xl tw-font-bold ${styles.titleText}`}>
            {title}
            <span className={`tw-ml-3 tw-text-2xl tw-font-bold ${styles.countText}`}>
              ({problems.length})
            </span>
          </h2>
          <RefreshIndicator
            isRefreshing={refreshing}
            lastUpdated={lastUpdated}
            showTimestamp={!refreshing && !!lastUpdated}
          />
        </div>
      )}

      {/* Mobile View: Card Grid */}
      {isMobile ? (
        <div className={`${styles.grid} tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6 tw-items-stretch`}>
          {problems.map((problem) => (
            <IncidentErrorBoundary
              key={problem.number || problem.id}
              incidentNumber={problem.number}
              onReset={() => {
                console.log(`Error boundary reset for incident ${problem.number}`);
              }}
            >
              <IncidentCard
                number={problem.number}
                priority={problem.priority}
                shortDescription={problem.shortDescription || problem.description || 'No description available'}
                status={problem.status}
                slackChannel={problem.slackChannel}
                incidentStart={problem.incidentStart}
                incidentEnd={problem.incidentEnd}
                affectedBrand={problem.affectedBrand}
                isExpanded={expandedCardId === problem.number}
                onToggleExpand={() => handleCardToggle(problem.number)}
                anyCardExpanded={!!expandedCardId}
                section="active_incidents"
                newrelic={newrelic}
                user={user}
              />
            </IncidentErrorBoundary>
          ))}
        </div>
      ) : (
        /* Desktop/Tablet View: List with Accordion */
        <>
          <div className={styles.listContainer}>
            {problems.map((problem) => (
              <IncidentErrorBoundary
                key={problem.number || problem.id}
                incidentNumber={problem.number}
                onReset={() => {
                  console.log(`Error boundary reset for incident ${problem.number}`);
                  // Clear selection if this incident was selected
                  if (selectedIncident?.number === problem.number) {
                    setSelectedIncident(null);
                  }
                }}
              >
                <div
                  className={styles.incidentRowContainer}
                >
                  <IncidentListRow
                    number={problem.number}
                    priority={problem.priority}
                    shortDescription={problem.shortDescription || problem.description || 'No description available'}
                    status={problem.status}
                    slackChannel={problem.slackChannel}
                    incidentStart={problem.incidentStart}
                    incidentEnd={problem.incidentEnd}
                    affectedBrand={problem.affectedBrand}
                    isExpanded={selectedIncident?.number === problem.number}
                    onToggle={() => {
                      setSelectedIncident((prev) =>
                        prev?.number === problem.number ? null : problem
                      );
                    }}
                    section="active_incidents"
                    newrelic={newrelic}
                  />
                  {selectedIncident?.number === problem.number && (
                    <div className={`${styles.accordionContent} ${styles.expanded}`}>
                      <div className={styles.accordionContentInner}>
                        <IncidentErrorBoundary
                          incidentNumber={problem.number}
                          onReset={() => {
                            console.log(`Error boundary reset for expanded content of incident ${problem.number}`);
                          }}
                        >
                          <ExpandedContent
                            incidentNumber={problem.number}
                            incidentTitle={problem.shortDescription || problem.description || 'No description available'}
                            data={incidentData}
                            loading={incidentLoading}
                            refreshing={incidentRefreshing}
                            lastUpdated={incidentLastUpdated}
                            section="active_incidents"
                            error={incidentError}
                            newrelic={newrelic}
                            user={user}
                            onImpactCreated={refreshIncident}
                          />
                        </IncidentErrorBoundary>
                      </div>
                    </div>
                  )}
                </div>
              </IncidentErrorBoundary>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

IncidentSection.propTypes = {
  title: PropTypes.string,
  problems: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  refreshing: PropTypes.bool,
  lastUpdated: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  error: PropTypes.object,
  emptyMessage: PropTypes.string,
  onRetry: PropTypes.func,
  newrelic: PropTypes.object,
  user: PropTypes.object,
  showTitle: PropTypes.bool,
};

export default IncidentSection;
